import React, { useCallback } from 'react';
import DOMPurify from 'dompurify';
import { DEFAULT_ANCHOR_LENGTH } from '../../types/qa';
import { CapabilityCard, type CapabilityRunView } from './CapabilityCard';
import type { CapabilityAction, QAMessage, SimpleCitation, ChunkClickData } from '../../types/qa';
import { LoadingBubble } from './LoadingBubble';
import { CitationChip } from './CitationChip';
import { pickInlineEuHistoricalCitation } from '../../utils/euHistoricalCompareLink';
import { ANCHOR_TEXT_MAX } from '../viewer/anchorBbox';

/**
 * Anchor phrases for the viewer, taken from the resolver's picked sentences.
 *
 * `computeAnchorBbox` locates a phrase in the page's own text_spans, so the
 * box is drawn over text actually found on the page rather than over a stored
 * rectangle. The head of the FIRST sentence and the tail of the LAST give it
 * the span to cover when evidence runs across several sentences.
 *
 * Deliberately capped: a whole sentence as the needle is brittle against PDF
 * text-layer quirks (word joiners, double spaces, hyphenation), while ~60
 * characters is distinctive enough to locate and short enough to survive them.
 */
/**
 * React key for one Sources chip.
 *
 * A citation's identity is the WHOLE tuple (family, block, section, chunk), not
 * whichever part happens to be present first. The previous `??` chain preferred
 * `section_id` over `chunk_id`, so two chunks of one section produced the same
 * key — observed live on staging as
 *
 *     Warning: Encountered two children with the same key, `sba-311532`.
 *
 * for citations chunk_id=2062673 and chunk_id=2062674, both section 311532. A
 * section spanning pages and being cited twice is ordinary, not an edge case.
 * React may then reuse one chip's element for the other across updates, which
 * hands a chip the wrong citation's click handler: every step downstream stays
 * correct and lands on the wrong evidence.
 *
 * The index is the LAST resort, not a component of every key: competitive_intel
 * citations carry no chunk and no section, and would otherwise all collapse onto
 * one key. Keeping ids in the key where they exist also means a chip keeps its
 * identity — and its focus and hover state — across a re-render.
 */
export function citationChipKey(
  c: {
    document_type?: string | undefined;
    // STRING, not number — block_id is an opaque id on the citation schema,
    // unlike the integer section_id / chunk_id beside it. Typing it as a number
    // here compiled only because the old inline `??` chain was never checked
    // against SimpleCitation; extracting it is what surfaced the mismatch.
    block_id?: string | number | null | undefined;
    section_id?: number | null | undefined;
    chunk_id?: number | null | undefined;
  },
  i: number,
): string {
  const family = c.document_type ?? '';
  if (c.block_id == null && c.section_id == null && c.chunk_id == null) {
    return `${family}-i${i}`;
  }
  return `${family}-${c.block_id ?? ''}-${c.section_id ?? ''}-${c.chunk_id ?? ''}`;
}


/** A citation's canonical family, or `null` when it names none.
 *
 * `''` is UNKNOWN, not a family of its own. `read_file` emits `family: ''` for
 * a corpus that has no `Family`, and coercing that absence into a real name is
 * the trap that made every US-label citation claim to be SBA (`2bf53b728`).
 * Mirrors `citation_span_resolve._canonical_family`, including `us_label` →
 * `label`: the agents layer addresses the reader by corpus while every citation
 * calls it `label`.
 */
function citationFamily(
  c: { document_type?: string | undefined; corpus?: string | null | undefined },
): string | null {
  for (const value of [c.document_type, c.corpus]) {
    if (!value) continue;
    return value === 'us_label' ? 'label' : value;
  }
  return null;
}


/** The spans belonging to `citation`, resolved by (kind, FAMILY, id).
 *
 * `chunk_id` and `section_id` are autoincrement values from PER-FAMILY tables
 * that share one integer namespace. Measured on staging 2026-10-02:
 * **2,674,068** ids exist in both `evidence_chunks` (label) and
 * `sba_evidence_chunks`, 321,586 in both sba and epar, and every family pair's
 * ranges overlap. A lookup on the number alone therefore resolves an answer's
 * EPAR chunk #875464 and its SBA chunk #875464 — two unrelated passages — to
 * whichever happens to be listed first.
 *
 * The backend already keys its citation dedup on `(family, id)` for exactly
 * this reason, so both colliding citations survive into `message.citations`;
 * this is the matching guard on the browser side.
 *
 * Three rules, mirrored from `citation_span_resolve` so both ends agree:
 *
 *  - MOST SPECIFIC WINS (`_identity`). A record that owns a chunk is identified
 *    by its chunk; only a chunk-less record falls back to its section. A unit
 *    that merely shares a citation's section is a different passage in that
 *    section, and drawing it would assert something the citation does not say.
 *  - An unknown family on EITHER side is a WILDCARD (`_bucket_for`), so records
 *    written before a family was resolvable still resolve.
 *  - Two survivors means the id collided across families, and **refusing is the
 *    only safe answer** — "a missing highlight says less, a wrong one asserts
 *    something false". The caller must then also drop the page override, or the
 *    viewer still lands on a page chosen by the other document's evidence.
 */
export function findCitationSpans<
  T extends {
    document_type?: string | undefined;
    corpus?: string | null | undefined;
    section_id?: number | null | undefined;
    chunk_id?: number | null | undefined;
    evidence_spans?: unknown;
  },
>(citations: readonly T[] | undefined, citation: T): T['evidence_spans'] {
  const kindOf = (c: T) =>
    c.chunk_id != null ? 'chunk' : c.section_id != null ? 'section' : null;
  const numberOf = (c: T) => (c.chunk_id != null ? c.chunk_id : c.section_id);

  const kind = kindOf(citation);
  if (kind === null) return undefined;
  const number = numberOf(citation);
  const want = citationFamily(citation);

  const hits = (citations ?? []).filter((c) => {
    if (kindOf(c) !== kind || numberOf(c) !== number) return false;
    const family = citationFamily(c);
    return want === null || family === null || family === want;
  });
  return hits.length === 1 ? hits[0]!.evidence_spans : undefined;
}


/** The citation an inline section-link's DOM id refers to, or `undefined`.
 *
 * Same integer-namespace hazard as `findCitationSpans` above, one layer up: a
 * `data-chunk-id` / `data-section-id` attribute is a bare autoincrement value
 * from a per-family table, so `citations.find(c => c.chunk_id === id)` returned
 * whichever family happened to be listed first and the click opened a different
 * document than the answer cited.
 *
 * Three differences from the chip rule, each deliberate:
 *
 *  - THE QUERY HAS NO FAMILY. A chip starts from a citation that knows its own
 *    `document_type`; a DOM attribute is an integer. The discriminators here are
 *    hints standing beside that integer, not properties of it.
 *  - SO IT NARROWS ONLY ON COLLISION. A single candidate is returned
 *    unconditionally, family or not: an EPAR chat may legitimately cite a label,
 *    and filtering on the corpus up front would drop exactly those. The hints
 *    are spent only when two candidates claim the same integer.
 *  - NO MOST-SPECIFIC-WINS. The DOM states the kind, so a section query matches
 *    on `section_id` even when the citation also owns a chunk — which every real
 *    SBA citation does. Deriving the kind instead would match none of them.
 *
 * Two hints, strongest first, because they are not equally informative:
 *
 *  1. `sectionNumber` — the link's own `data-section`. It describes THIS
 *     reference, and it is the one attribute the section-link prompt actually
 *     mandates (`epar_agent_service.py:219`), so it is nearly always present.
 *  2. `prefer` — the chat's corpus (`defaultRegulatoryFamily`). Ambient: it says
 *     what the reader is looking at, not what this link points to.
 *
 * REFUSING IS NOT FREE, which is why the chain exists. The fallback at the chunk
 * branch's tail is itself gated on a family, so a refusal in a chat with no
 * corpus is a DEAD CLICK, not a degraded one. Reaching that requires a collided
 * id AND identical section numbers AND no corpus — at which point the two
 * candidates are indistinguishable from everything the DOM carries, and opening
 * one of two unrelated documents at random is the worse failure: a wrong
 * document asserts something the answer never said, where nothing asserts
 * nothing. Same trade the server's `_bucket_for` makes.
 */
export function findCitationByDomId<
  T extends {
    document_type?: string | undefined;
    corpus?: string | null | undefined;
    section_id?: number | null | undefined;
    chunk_id?: number | null | undefined;
    section_number?: string | null | undefined;
  },
>(
  citations: readonly T[] | undefined,
  kind: 'chunk' | 'section',
  id: number,
  // `| undefined` spelled out, not implied: `exactOptionalPropertyTypes` is on,
  // so an optional property and one that may be passed as undefined are
  // different types, and both callers read attributes that can be either.
  hints: {
    sectionNumber?: string | null | undefined;
    prefer?: string | null | undefined;
  },
): T | undefined {
  let candidates = (citations ?? []).filter((c) =>
    kind === 'chunk' ? c.chunk_id === id : c.section_id === id);
  if (candidates.length <= 1) return candidates[0];

  const wantSection = hints.sectionNumber?.trim();
  if (wantSection) {
    const bySection = candidates.filter((c) => c.section_number === wantSection);
    if (bySection.length === 1) return bySection[0];
    if (bySection.length > 1) candidates = bySection;
  }

  const wantFamily = hints.prefer
    ? citationFamily({ document_type: hints.prefer })
    : null;
  if (wantFamily === null) return undefined;
  const byFamily = candidates.filter((c) => {
    const family = citationFamily(c);
    return family === null || family === wantFamily;
  });
  return byFamily.length === 1 ? byFamily[0] : undefined;
}



function anchorsFromSentences(
  sentences: { text?: string | undefined }[],
): { start: string; end: string; text: string } | null {
  const texts = sentences.map(s => (s.text || '').trim()).filter(Boolean);
  if (texts.length === 0) return null;
  const first = texts[0]!;
  const last = texts[texts.length - 1]!;
  const start = first.slice(0, 60);
  const end = last.length > 60 ? last.slice(-60) : last;
  // The WHOLE cited text as well as the two probes. The probes stay short
  // because the strict matching passes need them short — a long exact needle
  // dies on one OCR error. The fifth pass needs the opposite, a whole sentence,
  // which is what lets it tell two table rows apart on their dose alone.
  // Joined across sentences because `start` and `end` already span first to
  // last, so the text has to cover the same stretch.
  const text = texts.join(' ').slice(0, ANCHOR_TEXT_MAX);
  return start.length >= 8 ? { start, end, text } : null;
}


/**
 * Extract the verbatim sentence (or phrase) the assistant quoted near a
 * given ``<a class="section-link">`` element.
 *
 * Used ONLY on the regulatory-document citation-click path (SBA / EPAR /
 * UK PIL) to derive a tight, sentence-level anchor for the viewer's
 * text-span match.  The FDA-label drug-QA path (which uses
 * ``citation.anchor_start`` directly via ``onChunkClick``) is upstream
 * of this helper and unaffected.
 *
 * Strategy: assistant answers in this mode commonly read like
 *   `The line "Acetylcysteine Injection will be diluted in..." is provided in <a>Section 3</a>.`
 * The longest ``"…"`` excerpt within ~200 chars of the link (in either
 * direction) is almost always the quoted sentence.  When no quote is
 * found we return ``null`` and the caller falls back to the
 * chunk-boundary anchor (existing behaviour) — no regression.
 *
 * Defensive: short matches (< 15 chars) are dropped — they're usually
 * single words like "the leaflet" which don't help bbox tightening.
 */
function extractNearbyQuote(
  htmlContent: string | undefined,
  sectionLink: HTMLElement,
): string | null {
  if (!htmlContent) return null;

  // Locate the section-link tag in the raw HTML by its outerHTML
  // (rendered HTML preserves attribute order so a substring match is
  // safe enough; on miss we fall through to a centred-window scan).
  const linkHtml = sectionLink.outerHTML;
  const linkPos = htmlContent.indexOf(linkHtml);
  // Window: ~200 chars on each side of the link, or the whole content
  // when the link can't be located.
  const WINDOW = 250;
  const start = linkPos >= 0 ? Math.max(0, linkPos - WINDOW) : 0;
  const end = linkPos >= 0
    ? Math.min(htmlContent.length, linkPos + linkHtml.length + WINDOW)
    : htmlContent.length;
  const window = htmlContent.slice(start, end);

  // Pull all "..." substrings.  Both straight + curly quotes (the
  // backend emits straight ASCII but a Gemini response can sneak in
  // unicode "smart" quotes).
  const re = /[“"]([^“”"]{15,400})[”"]/g;
  const candidates: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(window)) !== null) {
    const text = m[1];
    if (text) candidates.push(text);
  }
  if (candidates.length === 0) return null;
  // Longest wins: the quoted sentence is almost always the longest
  // bracketed string near the link (drug names like "Blenrep" are
  // shorter, the cited sentence is multi-clause).
  candidates.sort((a, b) => b.length - a.length);
  return candidates[0] || null;
}


/**
 * Chat message bubble — user or assistant.
 * Assistant messages render HTML content with citation chips.
 */
export const MessageBubble: React.FC<{
  message: QAMessage;
  streamingStatus?: string | undefined;
  onCitationClick?: ((citation: SimpleCitation) => void) | undefined;
  onSectionLinkClick?: ((sectionNumber: string, drugId?: number) => void) | undefined;
  onChunkClick?: ((chunk: ChunkClickData) => void) | undefined;
  /**
   * Family hint for the regulatory_document chat mode.  Used as the
   * fallback ``version_type`` when a clicked section-id link can't find
   * its matching streamed citation — without this, EPAR clicks would
   * silently fall through to the SBA section-ref resolver and 404.
   */
  defaultRegulatoryFamily?: 'sba' | 'epar' | 'uk_pil' | 'ca_pm' | 'au' | 'eu' | 'jp_pdf' | 'zh_pdf' | 'ch_pdf' | 'de_pdf' | 'ie_pdf' | 'it_pdf' | 'fr_html' | 'approval_letter' | undefined;
  /** Start the recommended capability run as-is. Card hides the button when omitted. */
  /**
   * ``messageId`` identifies WHICH card launched, so the panel can spend this
   * one without muting a later offer in the same conversation.
   */
  onCapabilityRun?: ((action: CapabilityAction, messageId: string) => void) | undefined;
  /**
   * This card's run is starting or already launched. Drives the card's own
   * ``busy``, which disables both buttons.
   *
   * It was never passed. The card defaulted to ``busy=false`` and stayed live
   * through the whole run, so a click that looked like nothing invited another
   * — six Investigator jobs from one card on prod, 2026-09-21.
   */
  capabilityBusy?: boolean | undefined;
  /**
   * State of the run THIS card launched (starting/running/stopped/failed).
   * Replaces the offer with the run card while present.
   */
  capabilityRun?: CapabilityRunView | undefined;
  /** Open the capability tab pre-filled from the action's scope draft. */
  onCapabilityOpen?: ((action: CapabilityAction) => void) | undefined;
  /** Shell: this message's capability tab was opened for the user (D14). */
  capabilityOpened?: boolean | undefined;
  onCapabilityFocus?: ((action: CapabilityAction) => void) | undefined;
  /** Investigator run launched from this card completed (offer to answer from it). */
  capabilityDone?: { matched: number; onAnswer: () => void; onOpenTab?: (() => void) | undefined } | undefined;
  capabilityDoneBusy?: boolean | undefined;
  onOpenAgents?: (() => void) | undefined;
  /** Shell: re-send the user turn behind an interrupted reply. */
  onRetry?: ((messageId: string) => void) | undefined;
  /** Shell: in-app (`/…`) hrefs in an answer open a tab instead of reloading the page. */
  onOpenPath?: ((path: string) => void) | undefined;
}> = ({
  message,
  streamingStatus,
  onCitationClick: onCitationClickProp,
  onCapabilityRun,
  capabilityBusy = false,
  capabilityRun,
  onCapabilityOpen,
  capabilityOpened = false,
  onCapabilityFocus,
  capabilityDone,
  capabilityDoneBusy,
  onOpenAgents,
  onRetry,
  onOpenPath,
  onSectionLinkClick,
  onChunkClick,
  defaultRegulatoryFamily,
}) => {
  const isUser = message.role === 'user';

  /**
   * The single dispatch point for every citation click in this bubble.
   *
   * Reports the click to the citation-evidence resolver and then does exactly
   * what it did before. The report is SHADOW ONLY — fire-and-forget, response
   * never read, navigation untouched — so production behaviour is identical
   * whether the resolver is up, down, or disabled.
   *
   * It exists as a wrapper rather than a call at each of the ten dispatch
   * sites below so that a new citation route cannot be added without being
   * measured: routing through `onCitationClickProp` directly is the only way
   * to escape it, and nothing does.
   */
  const dispatchCitation = useCallback(
    (citation: SimpleCitation) => {
      // CAPTURED SPANS — preferred over the resolver, and checked first.
      //
      // `evidence_spans` are the sentences the model SAID it used, recorded at
      // answer time from the [E<n>] ids it cited. The resolver below does the
      // opposite: it takes the finished claim and scores the chunk's sentences
      // to guess which one was used, which fails exactly when the answer
      // paraphrases. A record beats a reconstruction, so when both are
      // available the record wins.
      //
      // It is also synchronous. The resolver path awaits a network round-trip
      // (~1.1s p50) before the page opens; this needs neither, because the
      // span was decided before the citation ever reached the browser.
      // Looked up from the MESSAGE, not read off the passed citation.
      //
      // Several dispatch routes rebuild the citation field by field (the
      // section-link path constructs a fresh `cit` and copies anchors across),
      // so a span read from the argument silently disappears on those routes —
      // it did, and three of these tests caught it. Looking the spans up from
      // the MESSAGE means a route cannot drop them by forgetting to copy a
      // field, which is the same reasoning that put every click through this
      // one function in the first place.
      //
      // Identity is (kind, FAMILY, id) and ambiguity refuses — see
      // `findCitationSpans`, which mirrors `citation_span_resolve` so both ends
      // of the wire agree. The rules live there rather than here so the two
      // cannot drift.
      const captured = findCitationSpans(message.citations, citation);
      if (captured && captured.length > 0) {
        const first = captured[0]!;
        // A span from a build older than 4f3aaae0a carries no `texts`. Empty
        // in means null out, which falls through to the resolver below —
        // exactly what happens for a citation with no span at all.
        const anchors = anchorsFromSentences(
          (first.texts ?? []).map((t) => ({ text: t })),
        );
        // EVERY span, each anchored on its OWN sentence. Taking only
        // `captured[0]` showed the reader a truthful but partial highlight on
        // 5 of 12 real citations — evidence on a second page simply never
        // appeared. Anchoring span 2 with span 1's text would be worse: it
        // would search that page for words printed on the other one, miss, and
        // look like a coverage gap rather than a bug.
        //
        // Per-span refusal: a span too short to locate is dropped on its own,
        // so it never costs a sibling its box and is never anchored on
        // something unfindable.
        // ONE ANCHOR PER SENTENCE, not one per span.
        //
        // `computeAnchorBbox` returns a single UNION rectangle from `start` to
        // `end`. Anchoring a span on its first sentence's head and its last
        // sentence's tail therefore boxed everything in between — on INFLECTRA
        // doc 1978 page 16, one span of 8 sentences drew a block from y 0.370
        // to y 0.810 that swallowed four bullets and two headings the answer
        // never cited. Measured against that page's own text_spans, each of the
        // six sentences in the neighbouring chunk resolves to its own tight
        // box (0.092-0.143, 0.126-0.178, 0.161-0.230, 0.248-0.335, 0.318-0.387,
        // 0.370-0.405), so the geometry was always available; the shape of the
        // anchor was throwing it away.
        //
        // A paragraph-sized highlight is not a smaller version of the right
        // answer. It asserts that text is evidence, and most of that text was
        // not cited.
        //
        // Per-sentence refusal: a sentence too short to anchor is dropped on
        // its own and costs its siblings nothing. `PageRenderer` dedupes on the
        // RESOLVED rectangle, so two sentences that land on one line collapse
        // to a single box rather than stacking opacity.
        const perSpan = captured
          .flatMap((span) =>
            (span.texts ?? []).map((text) => {
              const a = anchorsFromSentences([{ text }]);
              return a
                ? {
                    chunk_id: span.chunk_id ?? null,
                    page: span.page ?? null,
                    start: a.start,
                    end: a.end,
                    // This is the per-SENTENCE call, so `a.text` is exactly
                    // this sentence — which is what the fifth pass wants.
                    text: a.text,
                  }
                : null;
            }),
          )
          .filter((a): a is NonNullable<typeof a> => a !== null);
        if (anchors) {
          // TWO highlight mechanisms exist, and a captured span has to reach
          // the right one.
          //
          //   HTML label viewer : onChunkClick -> anchorMatchAndHighlight,
          //                       which paints via the CSS Highlight API
          //   PDF viewers       : the citation's anchors -> chunkAnchors ->
          //                       computeAnchorBbox -> a drawn box
          //
          // onChunkClick is otherwise only ever called from the section-link
          // handler, so a Sources CHIP on a US label could never text-
          // highlight — reported as "opened the doc, no highlight". Routing a
          // label span here is what closes that.
          // No chunk_id requirement. A US label is HTML, not PDF pages: the
          // highlight is anchorMatchAndHighlight over the section's DOM, which
          // needs the SECTION NUMBER and the sentence and nothing else —
          // doChunkHighlight never reads chunkId. Demanding one shut out exactly
          // the citations that are most precisely located, because a label
          // progressive read owns no chunk by construction.
          if (citation.document_type === 'label' && onChunkClick) {
            onChunkClick({
              chunkId: citation.chunk_id ?? null,
              sectionNumber: citation.section_number || '',
              anchorStart: anchors.start,
              anchorEnd: anchors.end,
              anchorLength: DEFAULT_ANCHOR_LENGTH,
            });
            return;
          }
          onCitationClickProp?.({
            ...citation,
            // Page, chunk key and anchor text must all name the SAME chunk.
            //
            // Downstream keys the anchor map by `citation.chunk_id` and the
            // renderer only consults chunks drawn on the page it opened. Taking
            // the page from the owner while leaving chunk_id as the chunk that
            // merely SHOWED the sentence sends the viewer to a page where that
            // chunk is not rendered, the lookup misses, and nothing is drawn —
            // silently, which is the worst version of this.
            //
            // A section-owned span (progressive read) carries no chunk of its
            // own, so the citation's stays: there is nothing better to point at.
            ...(first.chunk_id != null ? { chunk_id: first.chunk_id } : {}),
            ...(first.page != null ? { page: first.page } : {}),
            anchor_start: anchors.start,
            anchor_end: anchors.end,
            // The whole evidence, span by span. `anchor_start`/`anchor_end`
            // above stay pointing at the FIRST span so existing consumers are
            // untouched.
            ...(perSpan.length ? { evidence_anchors: perSpan } : {}),
            // The whole cited sentence, not just the 60-char probes above. A
            // consumer that highlights by TEXT LENGTH (the label handoff) would
            // otherwise box a fragment and imply the answer rested on that much.
            // Set here, on the fall-through, so it survives every dispatch route.
            ...((first.texts ?? []).length
              ? { evidence_text: (first.texts ?? []).join(' ') }
              : {}),
          });
          return;
        }
        // Anchors too short to match safely: fall through. A highlight that
        // cannot be located must not become a highlight drawn somewhere else.
      }

      onCitationClickProp?.(citation);
    },
    [onCitationClickProp, onChunkClick],
  );

  const handleContentClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    // External hyperlinks in the answer open in a NEW TAB so a click never
    // navigates the user away from the chat. Section-links (handled below)
    // keep their in-app navigation.
    const linkEl = target.closest('a[href]') as HTMLAnchorElement | null;
    if (linkEl && !linkEl.classList.contains('section-link')) {
      const href = linkEl.getAttribute('href') || '';
      if (/^https?:\/\//i.test(href)) {
        e.preventDefault();
        window.open(href, '_blank', 'noopener,noreferrer');
        return;
      }
      // A shell tab, not a full page load that would drop every tab's state.
      if (onOpenPath && href.startsWith('/') && !href.startsWith('//')) {
        e.preventDefault();
        onOpenPath(href);
        return;
      }
    }
    const sectionLink = target.closest('a.section-link') as HTMLElement | null;
    if (!sectionLink) return;

    e.preventDefault();
    e.stopPropagation();

    // Shadows the prop for the remainder of this handler. The guards
    // (`if (onCitationClick)`) keep their exact meaning: the wrapper is
    // undefined precisely when the host passed no handler.
    const onCitationClick = onCitationClickProp
      ? (citation: SimpleCitation) => dispatchCitation(citation)
      : undefined;

    // Chunk-level click — resolve chunk_id against message citations.
    // Two flows share the data-chunk-id attribute:
    //   1. FDA drug labels: citation has anchor_start → onChunkClick
    //      (text-anchor highlight inside section content).
    //   2. SBA documents: citation has section_id + chunk_id but no
    //      anchor_start → onCitationClick so the SBA viewer scrolls to
    //      the exact chunk's page+bbox. Looking up by chunk_id is what
    //      makes citations land on the correct page when a section spans
    //      many chunks across many pages.
    const chunkIdStr = sectionLink.getAttribute('data-chunk-id');
    if (chunkIdStr) {
      const chunkId = parseInt(chunkIdStr, 10);
      const citation = findCitationByDomId(
        message.citations, 'chunk', chunkId,
        {
          sectionNumber: sectionLink.getAttribute('data-section'),
          prefer: defaultRegulatoryFamily,
        },
      );

      // FDA-letter citation branch: must precede the anchor / regulatory
      // routing below so letter chunks don't fall through to the
      // chunk-anchor highlighter (which assumes a label/SBA viewer) or
      // the SBA/EPAR family dispatch (which would 404 letter clicks).
      // Letter citations always carry ``document_type='fda_letter'`` +
      // ``letter_id``; the hybrid linked-vs-unlinked URL routing lives
      // in the SearchPage onCitationClick handler.
      if (citation?.document_type === 'fda_letter' && citation.letter_id != null) {
        if (onCitationClick) {
          const letterCit: SimpleCitation = {
            section_number: citation.section_number || sectionLink.getAttribute('data-section') || '',
            section_title: citation.section_title || sectionLink.textContent || '',
            version_type: citation.version_type,
            document_type: 'fda_letter',
            letter_id: citation.letter_id,
            chunk_id: chunkId,
          };
          if (citation.drug_id != null) letterCit.drug_id = citation.drug_id;
          if (citation.section_id != null) letterCit.section_id = citation.section_id;
          onCitationClick(letterCit);
        }
        return;
      }

      // Task B4: label citation with drug_id (from ``investigate_drugs``).
      // Multi-drug answers route per-citation via ``onCitationClick`` so
      // the host page can navigate to the right drug.  Must precede the
      // legacy ``onChunkClick`` path below — that path assumes the chat
      // is already pinned to a single drug (drug_qa mode) and would
      // text-anchor-highlight inside the currently-viewed drug rather
      // than navigating to the citation's actual drug.
      if (
        citation?.document_type === 'label' &&
        citation.drug_id != null &&
        onCitationClick
      ) {
        const labelCit: SimpleCitation = {
          section_number: citation.section_number || sectionLink.getAttribute('data-section') || '',
          section_title: citation.section_title || sectionLink.textContent || '',
          version_type: citation.version_type,
          document_type: 'label',
          drug_id: citation.drug_id,
          chunk_id: chunkId,
        };
        if (citation.anchor_start != null) labelCit.anchor_start = citation.anchor_start;
        if (citation.anchor_end != null) labelCit.anchor_end = citation.anchor_end;
        if (citation.anchor_length != null) labelCit.anchor_length = citation.anchor_length;
        onCitationClick(labelCit);
        return;
      }

      if (citation?.anchor_start && onChunkClick) {
        onChunkClick({
          chunkId,
          sectionNumber: citation.section_number || sectionLink.getAttribute('data-section') || '',
          anchorStart: citation.anchor_start,
          anchorEnd: citation.anchor_end || '',
          anchorLength: citation.anchor_length || DEFAULT_ANCHOR_LENGTH,
        });
        return;
      }

      // Regulatory-document citations (SBA + EPAR + UK PIL) share this
      // routing — version_type carries the family so DrugDetailPage's
      // handler can pick the right viewer to scroll/highlight without
      // forking here.  If the streamed citation is missing or unfamilied,
      // fall back to ``defaultRegulatoryFamily`` so e.g. EPAR/UK PIL
      // clicks don't silently route through the SBA section-ref
      // resolver and 404.
      const familyFromCit =
        citation?.version_type === 'sba' ||
        citation?.version_type === 'epar' ||
        citation?.version_type === 'uk_pil' ||
        citation?.version_type === 'ca_pm' ||
        citation?.version_type === 'au' ||
        citation?.version_type === 'eu' ||
        citation?.version_type === 'jp_pdf' ||
        citation?.version_type === 'ch_pdf' ||
        citation?.version_type === 'de_pdf' ||
        citation?.version_type === 'ie_pdf' ||
        citation?.version_type === 'it_pdf' ||
        citation?.version_type === 'fr_html' ||
        citation?.version_type === 'approval_letter'
          ? citation.version_type
          : defaultRegulatoryFamily;
      if (familyFromCit && onCitationClick) {
        const cit: SimpleCitation = {
          section_number: citation?.section_number || sectionLink.getAttribute('data-section') || '',
          section_title: citation?.section_title || sectionLink.textContent || '',
          version_type: familyFromCit,
          chunk_id: chunkId,
        };
        if (citation?.section_id != null) cit.section_id = citation.section_id;
        if (citation?.start_char != null) cit.start_char = citation.start_char;
        if (citation?.end_char != null) cit.end_char = citation.end_char;
        if (citation?.document_id != null) cit.document_id = citation.document_id;
        if (citation?.document_type != null) cit.document_type = citation.document_type;
        if (citation?.drug_id != null) cit.drug_id = citation.drug_id;
        // Prefer a quoted excerpt extracted from the assistant's
        // answer text over the chunk-boundary anchor: the backend's
        // ``citation.anchor_start`` is the first 30 chars of the
        // whole chunk (designed for FDA-label drug-QA where chunks
        // are short), which collapses to the chunk's full paragraph
        // bbox in the viewer's text-span matcher.  The assistant's
        // own prose, on the other hand, usually quotes the exact
        // sentence in "..." right around the section link — using
        // that gives a tight, sentence-level highlight.  Falls back
        // silently to ``citation.anchor_start`` (existing behaviour)
        // when no quoted excerpt is found.
        const extractedQuote = extractNearbyQuote(
          message.content, sectionLink,
        );
        if (extractedQuote) {
          cit.anchor_start = extractedQuote;
        } else if (citation?.anchor_start != null) {
          cit.anchor_start = citation.anchor_start;
        }
        if (citation?.anchor_end != null) cit.anchor_end = citation.anchor_end;
        if (citation?.anchor_length != null) cit.anchor_length = citation.anchor_length;
        onCitationClick(cit);
        return;
      }
    }

    // Regulatory-document sections (SBA + EPAR) use data-section-id
    // (integer PK).  Fallback when data-chunk-id was missing or didn't
    // match a streamed citation — scrolls to the section's first chunk.
    // Family is carried via the matching citation's version_type so the
    // routing stays identical for both SBA and EPAR.
    const sectionIdStr = sectionLink.getAttribute('data-section-id');
    if (sectionIdStr && onCitationClick) {
      const sectionId = parseInt(sectionIdStr, 10);
      const matchingCit = findCitationByDomId(
        message.citations, 'section', sectionId,
        {
          sectionNumber: sectionLink.getAttribute('data-section'),
          prefer: defaultRegulatoryFamily,
        },
      );

      // FDA-letter section-id branch: place before the SBA/EPAR family
      // dispatch so letter section clicks build a letter-shaped citation
      // and route through SearchPage's linked-vs-unlinked URL handler
      // instead of falling through to the regulatory-document resolver.
      // Mirrors the inner `if (onCitationClick)` guard on the chunk-id
      // letter branch above for structural symmetry, even though the
      // outer `sectionIdStr && onCitationClick` gate already covers it.
      if (matchingCit?.document_type === 'fda_letter' && matchingCit.letter_id != null) {
        if (onCitationClick) {
          const letterCit: SimpleCitation = {
            section_number: matchingCit.section_number || sectionLink.getAttribute('data-section') || '',
            section_title: matchingCit.section_title || sectionLink.textContent || '',
            version_type: matchingCit.version_type,
            document_type: 'fda_letter',
            letter_id: matchingCit.letter_id,
            section_id: sectionId,
          };
          if (matchingCit.drug_id != null) letterCit.drug_id = matchingCit.drug_id;
          if (matchingCit.chunk_id != null) letterCit.chunk_id = matchingCit.chunk_id;
          onCitationClick(letterCit);
        }
        return;
      }

      const familyVersionType =
        (matchingCit?.version_type === 'sba' ||
         matchingCit?.version_type === 'epar' ||
         matchingCit?.version_type === 'uk_pil' ||
         matchingCit?.version_type === 'ca_pm' ||
         matchingCit?.version_type === 'au' ||
         matchingCit?.version_type === 'eu' ||
         matchingCit?.version_type === 'jp_pdf' ||
         matchingCit?.version_type === 'ch_pdf' ||
         matchingCit?.version_type === 'de_pdf' ||
         matchingCit?.version_type === 'ie_pdf' ||
         matchingCit?.version_type === 'it_pdf' ||
         matchingCit?.version_type === 'fr_html' ||
         matchingCit?.version_type === 'approval_letter')
          ? matchingCit.version_type
          : (defaultRegulatoryFamily ?? 'sba');
      const cit: SimpleCitation = {
        section_number: sectionLink.getAttribute('data-section') || '',
        section_title: sectionLink.textContent || '',
        version_type: familyVersionType,
        section_id: sectionId,
      };
      if (matchingCit?.chunk_id != null) cit.chunk_id = matchingCit.chunk_id;
      if (matchingCit?.start_char != null) cit.start_char = matchingCit.start_char;
      if (matchingCit?.end_char != null) cit.end_char = matchingCit.end_char;
      if (matchingCit?.document_id != null) cit.document_id = matchingCit.document_id;
      if (matchingCit?.document_type != null) cit.document_type = matchingCit.document_type;
      if (matchingCit?.drug_id != null) cit.drug_id = matchingCit.drug_id;
      if (matchingCit?.anchor_start) cit.anchor_start = matchingCit.anchor_start;
      if (matchingCit?.anchor_end) cit.anchor_end = matchingCit.anchor_end;
      if (matchingCit?.anchor_length != null) cit.anchor_length = matchingCit.anchor_length;
      onCitationClick(cit);
      return;
    }

    // Drug label sections use data-section (section_number string)
    const sectionNumber = sectionLink.getAttribute('data-section');
    const drugIdStr = sectionLink.getAttribute('data-drug-id');
    const drugId = drugIdStr ? parseInt(drugIdStr, 10) : undefined;

    // EU-historical change citations carry NO chunk_id / section_id — only
    // drug_id + section_number + the from/to version strings. An inline
    // section-link for one therefore lands here; match it by (drug_id,
    // section_number) and route to onCitationClick so the host opens the EU
    // historical COMPARE at that version pair (rather than the plain
    // onSectionLinkClick label scroll below). Must precede the label fallback.
    if (sectionNumber && drugId != null && onCitationClick) {
      // eu_historical → COMPARE; eu_history_section → INDIVIDUAL archived-version
      // viewer. Both carry only (drug_id, section_number) on the inline link, so
      // match either here and let the host route by ``document_type``. A given
      // answer normally uses one or the other (what-changed vs what-it-said).
      const matches = (message.citations || []).filter(
        c =>
          (c.document_type === 'eu_historical' ||
           c.document_type === 'eu_history_section') &&
          c.drug_id === drugId &&
          (c.section_number || '') === sectionNumber,
      );
      // A section can change across MORE THAN ONE interval. An inline "§X" link
      // carries no interval, so several distinct matching intervals are
      // genuinely ambiguous — pickInline returns null and we fall through to the
      // plain section scroll (the reader picks the interval from the dropdown)
      // rather than silently opening the first (the old ``.find()`` bug).
      const picked = pickInlineEuHistoricalCitation(matches);
      if (picked) {
        onCitationClick(picked);
        return;
      }
      if (matches.length > 0) {
        // Ambiguous historical evidence (several intervals/versions match this
        // §X). Do NOTHING — do NOT fall through to onSectionLinkClick below,
        // which scrolls the CURRENT SmPC (the wrong era). The reader must pick an
        // interval-labelled source chip to disambiguate.
        return;
      }
    }

    if (sectionNumber && onSectionLinkClick) {
      onSectionLinkClick(sectionNumber, drugId);
    }
    // ``defaultRegulatoryFamily`` is the DOM-id tie-break's corpus rung and it
    // is a PROP: omit it here and the handler spends the family the bubble
    // mounted with. Reachable — the family changes when the reader navigates to
    // another document, while ``useUnifiedChat`` resets messages only on a
    // ``chatScope`` change (uid + impersonated org), so the bubbles stay mounted.
  }, [onSectionLinkClick, onCitationClickProp, dispatchCitation, onChunkClick,
      onOpenPath, message.citations, message.content, defaultRegulatoryFamily]);

  if (message.isLoading) {
    return <LoadingBubble streamingStatus={streamingStatus} />;
  }

  // CEPI structured-output verifier affordance.
  //
  // First cut (whole-answer banner): if ANY segment came back
  // ``unverified`` we surface a single subtle chip. ``verified`` and
  // ``not_applicable`` need no badge. We FLAG, never strip — the answer
  // text below is rendered unchanged regardless of verdicts.
  //
  // The verdict ``reason``(s) make a good tooltip so the user can see WHY
  // a statement couldn't be verified.
  //
  // TODO(per-segment badges): the answer HTML wraps each segment in
  // ``<div class="cepi-segment" data-segment="{i}">``. A future pass can
  // pair each ``unverified`` verdict to its wrapper via
  // ``data-segment="{segment_index}"`` and decorate inline instead of (or
  // in addition to) this whole-answer banner.
  const unverified = (message.verification ?? []).filter(
    v => v.status === 'unverified',
  );
  const unverifiedReasons = unverified
    .map(v => v.reason)
    .filter(Boolean);
  const unverifiedTitle = unverifiedReasons.length > 0
    ? unverifiedReasons.join('\n')
    : 'Some statements in this answer could not be verified against the retrieved sources.';

  // A NON-ANSWER MUST NOT LOOK LIKE AN ANSWER.
  //
  // Both failure statuses arrive over a 200 with a human-readable body, so
  // rendered in the normal assistant bubble they read as a confident reply
  // that happens to be short. Styled distinctly instead, with no Sources
  // footer (there are none) and a heading that says which layer fell short:
  //
  //   evidence_incomplete  retrieval found nothing — rephrasing or narrowing
  //                        is the useful next move
  //   no_answer            evidence existed; the answer could not be written
  //                        over it — retrying is genuinely worth a shot
  //
  // Not red: neither is an error, and dressing "I could not find this" as a
  // crash invites the user to retry something that cannot succeed. Undefined
  // (older messages, or a build predating the field) is treated as ``ok``.
  const nonAnswer = message.answer_status === 'evidence_incomplete'
    || message.answer_status === 'no_answer';
  const nonAnswerHeading = message.answer_status === 'evidence_incomplete'
    ? 'No supporting evidence found'
    : 'Could not produce a grounded answer';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`rounded-2xl px-4 py-3 max-w-[85%] break-words overflow-hidden ${
          isUser
            ? 'bg-indigo-600 text-white rounded-br-md'
            : message.error
            ? 'bg-red-50 text-red-800 border border-red-200 rounded-bl-md'
            : nonAnswer
            ? 'bg-amber-50 text-amber-900 border border-amber-200 rounded-bl-md'
            : 'bg-slate-100 text-slate-800 rounded-bl-md'
        }`}
      >
        {isUser ? (
          <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
        ) : message.interrupted ? (
          <div data-testid="response-interrupted" className="flex items-center gap-2 text-sm">
            <span>Response interrupted</span>
            {onRetry && (
              <button
                type="button"
                onClick={() => onRetry(message.id)}
                data-testid="response-retry"
                className="rounded border border-red-300 bg-white px-2 py-0.5 text-xs font-medium text-red-700 hover:bg-red-50"
              >
                Retry
              </button>
            )}
          </div>
        ) : (
          <div>
            {nonAnswer && (
              <div
                className="flex items-center gap-1.5 mb-2 text-xs font-medium"
                data-testid="cepi-non-answer"
                data-answer-status={message.answer_status}
              >
                <svg
                  className="w-3.5 h-3.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{nonAnswerHeading}</span>
              </div>
            )}
            {unverified.length > 0 && (
              <div
                className="flex items-center gap-1.5 mb-2 px-2 py-1 rounded-md bg-amber-50 text-amber-800 border border-amber-200 text-xs cursor-help"
                title={unverifiedTitle}
              >
                <svg
                  className="w-3.5 h-3.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>Some statements could not be verified</span>
              </div>
            )}
            <div
              onClick={handleContentClick}
              className="text-sm prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0 qa-message-content"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message.content, { USE_PROFILES: { html: true } }) }}
            />
            {/* Capability handoff, after the answer and before Sources.
                Suppressed on a non-answer: offering to run a cohort job over
                187 products underneath "No supporting evidence found" asks
                the user to spend compute on a question we just said we could
                not retrieve for. */}
            {!nonAnswer && message.capability_action && (
              <CapabilityCard
                action={message.capability_action}
                onRun={(a) => onCapabilityRun?.(a, message.id)}
                onOpen={onCapabilityOpen}
                busy={capabilityBusy}
                run={capabilityRun}
                opened={capabilityOpened}
                onFocus={onCapabilityFocus}
                done={capabilityDone}
                doneBusy={capabilityDoneBusy}
                onOpenAgents={onOpenAgents}
              />
            )}
            {/* Never pair a Sources footer with a non-answer — "Sources (3)"
                under "No supporting evidence found" contradicts itself. The
                backend returns before emitting citations on both fail-closed
                paths, so this is belt-and-braces, not a live case. */}
            {!nonAnswer && message.citations && message.citations.length > 0 && (
              <div className="mt-2 pt-2 border-t border-slate-200">
                <div className="text-[11px] font-medium text-slate-500 mb-1">
                  Sources ({message.citations.length})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {message.citations.map((c, i) => (
                    <CitationChip
                      key={citationChipKey(c, i)}
                      citation={c}
                      index={i}
                      // No segment index: a Sources chip is deduped across the
                      // whole answer, so it belongs to no single claim. The
                      // backend resolves against every segment that cited this
                      // source and abstains if they disagree.
                      onClick={() => dispatchCitation(c)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
